import { useCallback, useContext, useState, useEffect, useMemo, Fragment } from "react";
import { useMediaQuery } from "react-responsive";
import Swal from "sweetalert2";
import { Transition } from "@headlessui/react";
import * as yup from "yup";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// Icons
import { ClipboardDocumentListIcon, PlusIcon, TrashIcon, } from "@heroicons/react/24/outline";

// Context
import SnackbarContext from "../../../context/psikotest/SnackbarContext";
import LoaderContext from "../../../context/psikotest/LoaderContext";

// Helper
import errorHandler from "../../../helper/psikotest/errorHandler";
import logRender from "../../../helper/psikotest/logRender";

// Controller
import Disc from "../../../controller/psikotest/psikotest_system/disc";
import DiscQuestionary from "../../../controller/psikotest/psikotest_system/disc_questionary";

// Component
import TableFilter from "../../../components/psikotest/psikotest_system/TableFilter";
import Table from "../../../components/psikotest/psikotest_system/Table";

// Modals
import FormQuestionary from "./modal/FormQuestionary";
import FormImportQuestionary from "./modal/FormImportQuestionary";

const schema = yup.object().shape({
  id: yup
    .number()
    .transform((value) => (isNaN(value) ? undefined : value))
    .required(),
  mdi: yup
    .string()
    .label("(MDI) Main Question | Description | Instructions")
    .transform((value) => (value === null ? "" : value))
    .required()
});

function List() {
  const { setMessage } = useContext(LoaderContext);
  const { setNotif } = useContext(SnackbarContext);
  // required state
  const [list, setList] = useState<Array<TPSDiscQuestionary>>([]);
  const [search, setSearch] = useState<string>("");
  const [focus_mdi, setFocusMdi] = useState<boolean>(false);
  // state modal
  const [isOpenFormQuestionary, setIsOpenFormQuestionary] = useState<boolean>(false);
  const [isOpenFormImportQuestionary, setIsOpenFormImportQuestionary] = useState<boolean>(false);
  // state other
  const [selected_disc_questionary, setSelectedDiscQuestionary] = useState<TPSDiscQuestionary | undefined>(undefined);

  // use form
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<TPSDisc>({ resolver: yupResolver(schema), mode: "onBlur" });


  useEffect(() => {
    logRender({ type: "page", name: "disc_questionary/List" });
  }, []);

  const detData = useCallback(async () => {
    try {
      setMessage("Fetch DISC Questionary");
      const disc_qc = new DiscQuestionary();
      const res_qc = await disc_qc.list();
      setList(res_qc.data.list);
      setMessage("");
    } catch (error) {
      setMessage("");
      const errorMessage = errorHandler(error);
      setNotif({ type: "error", message: errorMessage });
    }
  }, [setNotif, setMessage]);

  const getDoc = useCallback(
    async function getDoc() {
      try {
        // fetch rac document
        setMessage("Fetch Document");
        const cd = new Disc();
        const res = await cd.get();
        const row: TPSDisc = res.data.row;
        setValue("id", row.id);
        setValue("mdi", row.mdi);
        setMessage("");
      } catch (error) {
        setMessage("");
        const errorMessage = errorHandler(error);
        setNotif({ type: "error", message: errorMessage });
      }
    },
    [setValue, setNotif, setMessage]
  );

  useEffect(() => {
    getDoc();
    detData();
  }, [getDoc, detData]);

  const handleDeleteList = (id: number) => {
    const arrFilter = [...list].filter((v) => v.id !== id);
    setList(arrFilter);
  };

  // when selected_disc_questionary changes and not undefined open Cfit Questionary form to update
  useEffect(() => {
    if (selected_disc_questionary !== undefined) {
      setIsOpenFormQuestionary(true)
    }
  }, [selected_disc_questionary]);

  // handleRefreshAfterOpenForm
  const handleRefreshAfterOpenForm = () => {
    // rmove selected value will close form modal
    setSelectedDiscQuestionary(undefined)
    // close modal
    setIsOpenFormQuestionary(false)
    // re-fetch data
    detData();
  }

  // handleRefreshAfterImport
  const handleRefreshAfterImport = () => {
    // close import form modal
    setIsOpenFormImportQuestionary(false)
    // re-fetch data
    detData();
  }

  const onSubmit: SubmitHandler<TPSDisc> = async (data) => {
    try {
      setMessage("Save Disc");
      const cd = new Disc();
      let res = await cd.update(data);
      setNotif({ type: "success", message: res.data.message });
      setMessage("");
    } catch (error) {
      setMessage("");
      const errorMessage = errorHandler(error);
      setNotif({ type: "error", message: errorMessage });
    }
  };

  return (
    <div className="w-full flex flex-col space-y-0.5">
      {/* Import Questionary Form */}
      <Transition
        as={Fragment}
        show={isOpenFormImportQuestionary}
        enter="ease-out duration-50"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-50"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div>
          <FormImportQuestionary
            nextNumber={list.length + 1}
            handleClose={() => setIsOpenFormImportQuestionary(false)}
            handleRefreshAfterImport={handleRefreshAfterImport}
          />
        </div>
      </Transition>
      {/* Questionary Form */}
      <Transition
        as={Fragment}
        show={isOpenFormQuestionary}
        enter="ease-out duration-50"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-50"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div>
          <FormQuestionary data={selected_disc_questionary} nextNumber={list.length + 1} handleClose={() => handleRefreshAfterOpenForm()} />
        </div>
      </Transition>
      {/* Main Form */}
      <form className="w-full" onBlur={() => handleSubmit(onSubmit)()}>
        <div className="flex flex-wrap -mx-3">
          <input {...register("id")} type="number" className="hidden" />
          {/** (MDI) Main Question | Description | Instructions */}
          <div className="w-full md:w-full px-3 mb-6">
            <label className="text-gray-700 text-xs mb-2">
              <span className="block uppercase tracking-wide font-bold">(MDI) Main Question | Description | Instructions</span>
              <i>Main question/description/intructions for sub-test, always show up until test ended.</i>
            </label>
            <textarea
              className={
                (errors?.mdi ? "border-red-500" : "border-gray-200 focus:border-gray-500") +
                " first-letter:appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-2 px-3 focus:outline-none focus:bg-white disabled:text-gray-500"
              }
              rows={4}
              {...register("mdi")}
              onFocus={() => setFocusMdi(true)}
              onBlur={() => setFocusMdi(false)}
            />
            {errors.mdi && <p className="text-red-500 text-xs italic">{errors.mdi.message}</p>}
            <label className="text-xs mb-2">
              {focus_mdi
                ? <span className="text-green-600 block uppercase tracking-wide font-bold">AUTOSAVE ON OUT FOCUS</span>
                : <span className="text-blue-600 block uppercase tracking-wide font-bold">CLICK IN TEXTAREA TO EDIT</span>
              }
            </label>
            <input {...register("id")} type="number" className="hidden" />
          </div>
        </div>
      </form>
      <div className="flex flex-col md:flex-row items-start md:items-center">
        <div className="flex space-x-1">
          <button type="button"
            onClick={() => { setIsOpenFormImportQuestionary(true) }}
            className="block w-auto mb-2 md:m-0 px-4 py-2 bg-green-500 hover:bg-green-700 text-white rounded font-semibold text-sm"
          >
            <div className="flex">
              <ClipboardDocumentListIcon className="w-4" />
              <span className="block pl-1">Import</span>
            </div>
          </button>
          <button
            type="button"
            onClick={() => { setIsOpenFormQuestionary(true) }}
            className="block w-auto mb-2 md:m-0 px-4 py-2 bg-blue-500 hover:bg-blue-700 text-white rounded font-semibold text-sm"
          >
            <div className="flex">
              <PlusIcon className="w-5 h-5 mr-2 stroke-white fill-tranparent" aria-hidden="true" />
              Create
            </div>
          </button>
        </div>
        <div className="w-full md:w-auto ml-auto flex">
          <TableFilter search={setSearch} />
        </div>
      </div>
      <div className="py-5">
        <DiscQuestionaryList list={list} handleDeleteList={handleDeleteList} search={search} setSelectedDiscQuestionary={setSelectedDiscQuestionary} />
      </div>
    </div >
  );
}

function DiscQuestionaryList(props: {
  list: Array<TPSDiscQuestionary>;
  handleDeleteList: (id: number) => void;
  search: string,
  setSelectedDiscQuestionary: React.Dispatch<React.SetStateAction<TPSDiscQuestionary | undefined>>
}) {
  const setNotif = useContext(SnackbarContext).setNotif;
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const confirmDelete = useCallback(
    async (disc_q: TPSDiscQuestionary) => {
      try {
        const confirm = await Swal.fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, delete it!",
        });
        if (confirm.isConfirmed) {
          const disc_qc = new DiscQuestionary();
          await disc_qc.delete(disc_q.id);
          props.handleDeleteList(disc_q.id);
          setNotif({ type: "success", message: "disc questionary deleted" });
        }
      } catch (error) {
        const errorMessage = errorHandler(error);
        setNotif({ type: "error", message: errorMessage });
      }
    },
    [props, setNotif]
  );

  const columns = useMemo(
    () => [
      {
        id: "mobile",
        Header: "Number",
        accessor: "number",
        show: isMobile,
        Cell: (colProps: { row: { original: TPSDiscQuestionary } }) => {
          return (
            <button className="text-blue-500 hover:text-blue-800" onClick={() => props.setSelectedDiscQuestionary(colProps.row.original)}>
              {colProps.row.original.number || "NULL"}
            </button>
          );
        },
      },
      {
        id: "number",
        classHeader: "text-left px-2 w-20",
        Header: <div title="Number">Number</div>,
        classDivHeader: "flex items-center",
        accessor: "number",
        show: !isMobile,
        Cell: (colProps: { row: { original: TPSDiscQuestionary } }) => {
          return (
            <button className="flex items-center text-blue-500 hover:text-blue-800" onClick={() => props.setSelectedDiscQuestionary(colProps.row.original)}>
              {colProps.row.original.number}
            </button>
          );
        },
      },
      {
        id: "value_most",
        classHeader: "text-left px-2",
        Header: <div title="Value Most">Value Most</div>,
        classDivHeader: "flex items-center",
        accessor: "value_most",
        show: !isMobile,
        Cell: (props: { row: { original: TPSDiscQuestionary } }) => {
          return props.row.original.value_most
        },
      },
      {
        id: "value_least",
        classHeader: "text-left px-2",
        Header: <div title="Value Least">Value Least</div>,
        classDivHeader: "flex items-center",
        accessor: "value_least",
        show: !isMobile,
        Cell: (props: { row: { original: TPSDiscQuestionary } }) => {
          return props.row.original.value_least
        },
      },
      {
        id: "statement",
        classHeader: "text-left px-2",
        Header: <div title="Statement">Statement</div>,
        classDivHeader: "flex items-center",
        accessor: "statement",
        show: !isMobile,
        Cell: (props: { row: { original: TPSDiscQuestionary } }) => {
          return props.row.original.statement
        },
      },
      {
        id: "action_delete",
        classHeader: "w-[30px] text-white",
        Header: () => {
          return <TrashIcon className="h-5 w-auto" />;
        },
        classDivHeader: "flex justify-center",
        accessor: "id",
        show: !isMobile,
        disableSortBy: true,
        className: "text-white bg-red-500 hover:bg-red-800",
        Cell: (props: { row: { original: TPSDiscQuestionary } }) => {
          return (
            <button className="flex justify-center" onClick={() => confirmDelete(props.row.original)}>
              <TrashIcon className="h-5" />
            </button>
          );
        },
      },
    ],
    [isMobile, confirmDelete, props]
  );

  return <Table columns={columns} list={props.list} search={props.search} />;
}

export default List;
